import { isTuningHistogram, TUNING_HISTOGRAM_EDGES_MS } from '../contracts/measurements';
import type { TuningHistogram, TuningTelemetryWindow } from '../contracts/measurements';
import type { TuningTrialScore } from '../contracts/experiments';
import type { ITrialScoreManager, TrialScoreInput } from './OperatorContracts';

type Distribution = { count: number; max: number; counts: Map<number, number>; duration: number; invalid: boolean };
const measured = (window: TuningTelemetryWindow, name: string): number | undefined => {
  const metric = window.metrics[name]; return metric?.availability === 'measured' && metric.value !== null ? metric.value : undefined;
};
const duration = (window: TuningTelemetryWindow) => Math.max(0, window.monotonicEndMs - window.monotonicStartMs);

/** Whole-segment bins are merged; window quantiles are never used as trial distributions. */
export class TrialScoreManager implements ITrialScoreManager {
  public score(input: TrialScoreInput): TuningTrialScore {
    const windows = input.windows.filter(window => window.target.sessionId === input.snapshot.target.sessionId
      && window.target.trialId === input.snapshot.target.trialId && window.target.connectionEpoch === input.snapshot.target.connectionEpoch
      && window.target.configRevision === input.snapshot.target.configRevision);
    const host = windows.filter(window => window.endpoint === 'host');
    const guest = windows.filter(window => window.endpoint === 'guest');
    const reasons: string[] = []; const failed: string[] = []; const invalid: string[] = [];
    const visible = guest.filter(window => window.visibility === 'visible' && measured(window, 'presentedFps') !== undefined);
    const coverage = Math.min(1, visible.reduce((sum, window) => sum + duration(window), 0) / input.measurementMs);
    if (coverage < 0.95) reasons.push('insufficient-visible-coverage');
    const hostCoverage = host.filter(window => measured(window, 'encode.fps') !== undefined)
      .reduce((sum, window) => sum + duration(window), 0) / input.measurementMs;
    if (hostCoverage < 0.95) reasons.push('insufficient-host-cadence-coverage');
    if (!host.length || !guest.length) reasons.push('missing-endpoint-evidence');
    for (const group of [host, guest]) if (this.changed(group)) invalid.push('measurement-segment-changed');
    const dropped = this.counterDelta(guest, 'telemetryDropped');
    if (dropped > input.targets.maxTelemetryDropFraction * (guest.length + dropped)) reasons.push('telemetry-loss');
    const histogramDrops = guest.map(window => measured(window, 'histogramDroppedSamples')).filter(value => value !== undefined) as number[];
    if (histogramDrops.length > 1 && histogramDrops.at(-1)! > histogramDrops[0]) reasons.push('histogram-samples-dropped');
    const hostFps = this.average(host, 'encode.fps'); const presented = this.average(visible, 'presentedFps');
    const fraction = hostFps !== undefined && hostFps > 0 && presented !== undefined ? presented / hostFps : undefined;
    if (fraction === undefined) reasons.push('missing-observed-cadence');
    else if (fraction > 2) invalid.push('inconsistent-endpoint-cadence');
    else if (fraction < input.targets.minPresentedFraction) failed.push('presented-cadence-below-target');
    const gaps = this.merge(visible, 'presentationGap');
    const captureLatency = input.targets.latencyEndpoint === 'capture-to-composition';
    const latencyName = captureLatency ? 'captureToComposition' : 'encodeToCompositionMs';
    const latencyWindows = captureLatency ? visible : host;
    if (!captureLatency && this.metricCounterIncreased(host, 'observer.timingSamplesDropped')) reasons.push('latency-samples-dropped');
    const latency = this.merge(latencyWindows, latencyName);
    if (gaps.invalid || latency.invalid) invalid.push('overlapping-or-invalid-histogram');
    if (gaps.duration < input.measurementMs * 0.95 || latency.duration < input.measurementMs * 0.95) reasons.push('insufficient-histogram-coverage');
    const bounds: TuningTrialScore['quantileBounds'] extends Readonly<infer MapType> ? MapType : never = {};
    const gap95 = this.quantile(gaps, 0.95, 40); const gap99 = this.quantile(gaps, 0.99, 200);
    const lat50 = this.quantile(latency, 0.5, 40); const lat95 = this.quantile(latency, 0.95, 40);
    const lat99 = this.quantile(latency, 0.99, 200);
    if (gap95) bounds['gapP95'] = gap95; if (gap99) bounds['gapP99'] = gap99;
    if (lat50) bounds['latencyP50'] = lat50; if (lat95) bounds['latencyP95'] = lat95; if (lat99) bounds['latencyP99'] = lat99;
    this.threshold(gap99, input.targets.maxGapP99Ms, 'gap-p99', failed, reasons);
    const largestGap = Math.max(gaps.max, ...visible.map(window => Math.max(
      measured(window, 'presentationGap.max') ?? 0, measured(window, 'sinceLastPresent') ?? 0)));
    if (visible.length && largestGap > input.targets.maxGapMs) failed.push('maximum-gap-breach');
    const clocks = latencyWindows.filter(window => window.distributions[latencyName]?.sampleCount);
    const calibrated = clocks.length > 0 && clocks.every(window => window.clock.state === 'calibrated'
      && Number.isFinite(window.clock.uncertaintyMs) && window.clock.uncertaintyMs! >= 0
      && Number.isFinite(window.clock.ageMs) && window.clock.ageMs! >= 0 && window.clock.ageMs! <= 60_000);
    const uncertainty = calibrated ? Math.max(...clocks.map(window => window.clock.uncertaintyMs!)) : undefined;
    if (!calibrated) reasons.push('uncalibrated-latency');
    else this.threshold(lat95 && { lowerMs: Math.max(0, lat95.lowerMs - uncertainty!),
      upperMs: lat95.upperMs === null ? null : lat95.upperMs + uncertainty! }, input.targets.maxLatencyP95Ms, 'latency-p95', failed, reasons);
    const result = invalid.length ? 'invalid' : failed.length ? 'fail' : reasons.length ? 'inconclusive' : 'pass';
    return { trialId: input.snapshot.target.trialId, configurationHash: input.snapshot.configurationHash, result,
      reasonCodes: [...new Set([...invalid, ...failed, ...reasons])], coverageFraction: coverage, quantileBounds: bounds,
      ...(fraction === undefined || fraction > 2 ? {} : { presentedFraction: fraction }),
      ...(gap95?.upperMs === null || !gap95 ? {} : { gapP95Ms: gap95.upperMs }),
      ...(gap99?.upperMs === null || !gap99 ? {} : { gapP99Ms: gap99.upperMs }),
      ...(gaps.count || visible.length ? { maxGapMs: largestGap } : {}),
      ...(lat50?.upperMs === null || !lat50 ? {} : { latencyP50Ms: lat50.upperMs }),
      ...(lat95?.upperMs === null || !lat95 ? {} : { latencyP95Ms: lat95.upperMs }),
      ...(lat99?.upperMs === null || !lat99 ? {} : { latencyP99Ms: lat99.upperMs }),
      latencyEndpoint: input.targets.latencyEndpoint, imageQuality: { kind: 'unmeasured' } };
  }
  private average(windows: readonly TuningTelemetryWindow[], name: string): number | undefined {
    let total = 0; let span = 0;
    for (const window of windows) { const value = measured(window, name); if (value === undefined) continue;
      const milliseconds = duration(window); total += value * milliseconds; span += milliseconds; }
    return span > 0 ? total / span : undefined;
  }
  private changed(windows: readonly TuningTelemetryWindow[]): boolean {
    if (!windows.length) return false;
    const identities = new Set(windows.map(window => JSON.stringify([window.pathEpoch, window.captureGeneration,
      window.sourceId, window.endpointConnectionEpoch, window.context?.codec, window.context?.route,
      window.context?.configurationHash, window.metrics['frameWidth']?.value, window.metrics['frameHeight']?.value])));
    // Slow context may be omitted on sparse windows; compare each observed field independently.
    if (identities.size === 1) return false;
    for (const read of [(w: TuningTelemetryWindow) => w.pathEpoch, (w: TuningTelemetryWindow) => w.captureGeneration,
      (w: TuningTelemetryWindow) => w.sourceId, (w: TuningTelemetryWindow) => w.endpointConnectionEpoch,
      (w: TuningTelemetryWindow) => w.context?.codec, (w: TuningTelemetryWindow) => w.context?.route,
      (w: TuningTelemetryWindow) => w.context?.configurationHash, (w: TuningTelemetryWindow) => measured(w, 'frameWidth'),
      (w: TuningTelemetryWindow) => measured(w, 'frameHeight')]) {
      if (new Set(windows.map(window => read(window)).filter(value => value !== undefined)).size > 1) return true;
    }
    return false;
  }
  private counterDelta(windows: readonly TuningTelemetryWindow[], name: 'telemetryDropped'): number {
    const first = windows[0]?.[name] ?? 0; const last = windows.at(-1)?.[name] ?? first;
    return Math.max(0, last - first);
  }
  private metricCounterIncreased(windows: readonly TuningTelemetryWindow[], name: string): boolean {
    const values = windows.map(window => measured(window, name)).filter((value): value is number => value !== undefined);
    return values.some((value, index) => index > 0 && value > values[index - 1]);
  }
  private merge(windows: readonly TuningTelemetryWindow[], name: string): Distribution {
    const result: Distribution = { count: 0, max: 0, counts: new Map(), duration: 0, invalid: false };
    let lastEnd: number | undefined;
    for (const window of windows) {
      const histogram: TuningHistogram | undefined = window.distributions[name]; if (!histogram?.sampleCount) continue;
      if (!isTuningHistogram(histogram) || histogram.availability !== 'measured'
        || (lastEnd !== undefined && histogram.monotonicStartMs < lastEnd)) { result.invalid = true; continue; }
      lastEnd = histogram.monotonicEndMs; result.duration += histogram.monotonicEndMs - histogram.monotonicStartMs;
      result.count += histogram.sampleCount; result.max = Math.max(result.max, histogram.maxMs ?? 0);
      for (const [index, count] of histogram.buckets) result.counts.set(index, (result.counts.get(index) ?? 0) + count);
    }
    return result;
  }
  private quantile(distribution: Distribution, quantile: number, minimum: number) {
    if (distribution.count < minimum) return undefined;
    const rank = Math.ceil(quantile * distribution.count); let cumulative = 0;
    for (const [index, count] of [...distribution.counts].sort(([a], [b]) => a - b)) {
      cumulative += count; if (cumulative < rank) continue;
      const upper = TUNING_HISTOGRAM_EDGES_MS[index + 1];
      return { lowerMs: TUNING_HISTOGRAM_EDGES_MS[index], upperMs: Number.isFinite(upper) ? upper : null };
    }
    return undefined;
  }
  private threshold(bounds: { lowerMs: number; upperMs: number | null } | undefined, target: number,
    name: string, failed: string[], reasons: string[]): void {
    if (!bounds) reasons.push(`${name}-insufficient-samples`);
    else if (bounds.lowerMs > target) failed.push(`${name}-breach`);
    else if (bounds.upperMs === null || bounds.upperMs > target) reasons.push(`${name}-uncertain-bin`);
  }
}
